import { memo, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import { analyticsActions } from '@actions';
import MetricItem from '@features/Analytics/Common/MetricItem';
import DoughnutItem from '@features/Analytics/Common/DoughnutItem';
import SentimentTrend from '@features/Analytics/Common/SentimentTrend';
import ReviewsByDay from '@features/Analytics/Common/ReviewsByDay';
import ReviewsByDaypart from '@features/Analytics/Common/ReviewsByDaypart';
import { AnalyticsContainer } from '@features/Analytics/styles';
import { useTranslation } from '@hooks';
import RatingsTimeline from './RatingsTimeline';
import ReviewsChannelTrend from './ReviewsChannelTrend';
import ReviewsTable from './ReviewsTable';

const ReviewsOverviewGrid = styled('div')({
    display: 'grid',
    gridGap: 16,
    marginBottom: 16,
    gridTemplateColumns:
        'minmax(200px, 240px) minmax(200px, 240px) minmax(380px, 1.2fr) minmax(280px, 1fr) minmax(280px, 1fr)',
    gridTemplateAreas: '"metric1 metric3 doughnut byday daypart" "metric2 metric4 doughnut byday daypart"',
    '@media (max-width: 1780px)': {
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gridTemplateAreas:
            '"metric1 metric2 doughnut doughnut" "metric3 metric4 doughnut doughnut" "byday byday daypart daypart"',
    },
    '@media (max-width: 1120px)': {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gridTemplateAreas: '"metric1 metric2" "metric3 metric4" "doughnut doughnut" "byday byday" "daypart daypart"',
    },
    '@media (max-width: 600px)': {
        gridTemplateColumns: '100%',
        gridTemplateAreas: '"metric1" "metric2" "metric3" "metric4" "doughnut" "byday" "daypart"',
    },
    '@media print': {
        gridTemplateColumns: '100%',
        gridTemplateAreas: '"metric1" "metric2" "metric3" "metric4" "doughnut" "byday" "daypart"',
    },
});

const Reviews = memo(({ filter, filterVersion }) => {
    const lastFetchedVersion = useRef(-1);

    const { t } = useTranslation();
    const language = useSelector(state => state.auth.user?.language);
    const { reviewsData, reviewsLoading } = useSelector(state => state.analytics);
    const dispatch = useDispatch();

    const currentQuery = useMemo(() => {
        return {
            locations: filter.locations.map(item => item.id),
            profileTypes: filter.integrations,
            from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
            to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            grouping: filter.grouping,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
    }, [filter]);

    useEffect(() => {
        if (filterVersion > lastFetchedVersion.current && filter.locations.length && filter.integrations.length) {
            dispatch(analyticsActions.getAnalyticsReviews(currentQuery));
            lastFetchedVersion.current = filterVersion;
        }
    }, [filterVersion, currentQuery]);

    const overall = reviewsData.overall;

    return (
        <AnalyticsContainer id="pl-content-analytics-reviews">
            <ReviewsOverviewGrid>
                <MetricItem
                    title={t('AnalyticsReviews.averageRating')}
                    data={{ metric: overall.averageRating, delta: overall.averageRatingDelta }}
                    styles={{ gridArea: 'metric1' }}
                />
                <MetricItem
                    title={t('AnalyticsReviews.totalReviews')}
                    data={{ metric: overall.totalReviews, delta: overall.totalReviewsDelta }}
                    styles={{ gridArea: 'metric2' }}
                    withPercentageDelta
                />
                <MetricItem
                    title={t('AnalyticsReviews.totalRepliesPercentage')}
                    data={{ metric: overall.replyRate, delta: overall.replyRateDelta }}
                    styles={{ gridArea: 'metric3' }}
                    withPercentage
                    withPercentageDelta
                />
                <MetricItem
                    title={t('AnalyticsReviews.reviewIndex')}
                    tooltip={t('AnalyticsReviews.reviewIndexTooltip')}
                    data={{ metric: overall.reviewIndex, delta: overall.reviewIndexDelta }}
                    notation="standard"
                    styles={{ gridArea: 'metric4' }}
                />
                <DoughnutItem data={overall} isLoading={reviewsLoading} type="reviews" />
                <ReviewsByDay data={reviewsData.dayOfWeekDistribution} styles={{ gridArea: 'byday' }} />
                <ReviewsByDaypart data={reviewsData.daypartDistribution} styles={{ gridArea: 'daypart' }} />
            </ReviewsOverviewGrid>
            <RatingsTimeline data={reviewsData.ratingsTimeline} language={language} grouping={filter.grouping} />
            <SentimentTrend
                data={reviewsData.sentimentData}
                language={language}
                type="reviews"
                grouping={filter.grouping}
            />
            <ReviewsChannelTrend data={reviewsData.channelData} language={language} grouping={filter.grouping} />
            <ReviewsTable data={reviewsData.tableData} isLoading={reviewsLoading} filter={filter} />
        </AnalyticsContainer>
    );
});

Reviews.displayName = 'Reviews';

export default Reviews;
